'use client'

import { useState, useEffect, useRef } from 'react'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'
import TicketSelector from './TicketSelector'
import BookingSummary from './BookingSummary'
import LoadingSpinner from '../common/LoadingSpinner'
import type { TimedUITicketType, DayTabConfig } from '@/lib/timed-ui/adapters'

interface BookingPanelProps {
  ticketTypes: TimedUITicketType[]
  regularTickets: TimedUITicketType[]
  selectedTickets: Record<string, number>
  onTicketQuantityChange: (ticketTypeId: string, quantity: number) => void
  totalPrice: number
  ticketCount: number
  onCheckout: () => void
  loadingTickets: boolean
  checkoutLoading: boolean
  slotCapacity?: number
  /** Cap across all ticket types in the order (a sponsor link's limit). */
  orderLimit?: number | null
  compact?: boolean
  dayTabConfig?: DayTabConfig | null
  timezone?: string
}

const BookingPanel = ({
  ticketTypes,
  regularTickets,
  selectedTickets,
  onTicketQuantityChange,
  totalPrice,
  ticketCount,
  onCheckout,
  loadingTickets,
  checkoutLoading,
  slotCapacity,
  orderLimit,
  compact = false,
  dayTabConfig,
  timezone,
}: BookingPanelProps) => {
  const formatPrice = useFormatPrice()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const prevHasTickets = useRef(false)
  const hasTickets = ticketCount > 0
  const showTickets = ticketTypes.length > 0 || loadingTickets

  const totalItemCount = ticketCount

  useEffect(() => {
    if (hasTickets && !prevHasTickets.current && scrollContainerRef.current) {
      setTimeout(() => {
        scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' })
      }, 150)
    }
    prevHasTickets.current = hasTickets
  }, [hasTickets])

  return (
    <div className={`${compact ? '' : 'sticky top-20'} bg-surface border border-surface-border rounded-2xl shadow-panel ${compact ? 'flex flex-col max-h-[70vh]' : ''}`}>
      <div className={`bg-gradient-to-r from-primary/5 to-primary/10 border-b border-surface-border rounded-t-2xl ${compact ? 'shrink-0 px-4 py-3' : 'px-6 py-4'}`}>
        <h2 className={`font-heading font-bold text-text-primary ${compact ? 'text-base' : 'text-lg'}`}>
          Book Your Experience
        </h2>
        <p className="text-xs text-text-tertiary mt-0.5">Select your preferred tickets</p>
      </div>

      <div ref={scrollContainerRef} className={`space-y-6 ${compact ? 'flex-1 overflow-y-auto min-h-0 scrollbar-hide p-4' : 'p-6'}`}>
        {showTickets && (
          <div>
            <h3 className="text-base font-heading font-bold text-text-primary mb-3">
              Select Tickets
            </h3>
            {loadingTickets ? (
              <div className="py-8">
                <LoadingSpinner size="sm" text="Loading tickets..." />
              </div>
            ) : (
              <TicketSelector
                ticketTypes={regularTickets.length > 0 ? regularTickets : ticketTypes}
                selectedTickets={selectedTickets}
                onQuantityChange={onTicketQuantityChange}
                slotCapacity={slotCapacity}
                orderLimit={orderLimit}
                dayTabConfig={dayTabConfig}
                timezone={timezone}
              />
            )}
          </div>
        )}

        {showTickets && (
          <BookingSummary
            totalPrice={totalPrice}
            ticketCount={ticketCount}
          />
        )}
      </div>

      {totalItemCount > 0 && (
        <div className={`border-t border-surface-border bg-surface animate-slide-up rounded-b-2xl ${compact ? 'shrink-0 px-4 py-3' : 'px-6 py-4'}`}>
          <button
            onClick={onCheckout}
            disabled={checkoutLoading}
            className={`w-full bg-primary hover:bg-primary-hover text-text-inverse font-bold rounded-xl transition-all duration-200 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-sm hover:shadow-md ${compact ? 'py-3 text-base' : 'py-4 text-lg'}`}
          >
            {checkoutLoading ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Processing...
              </>
            ) : (
              <>
                <span>Checkout</span>
                <span className="bg-text-inverse/20 px-2.5 py-0.5 rounded-lg text-sm">
                  {totalItemCount} {totalItemCount === 1 ? 'item' : 'items'} · {formatPrice(totalPrice)}
                </span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  )
}

export default BookingPanel
